import api from './api';
import {
  MailSignature,
  MailSignatureRequest,
  OutreachContact,
  OutreachContactDetail,
  OutreachContactStatus,
  OutreachFile,
  OutreachMailbox,
  OutreachMessage,
  OutreachNote,
  OutreachNoteRequest,
  OutreachTeamMember,
  OutreachDraft,
  OutreachDraftRequest,
  OutreachOrganizerDiff,
  OutreachPlaceholder,
  OutreachSuppression,
  OutreachSuppressionReason,
  OutreachTemplate,
  CreateOutreachContactRequest,
  UpdateOutreachContactRequest,
  CreateOutreachTemplateRequest,
  UpdateOutreachTemplateRequest,
  SendOutreachEmailRequest,
  InviteOrganizersRequest,
  InviteOrganizersResult,
} from '@radevents/shared';

export interface RenderedTemplate {
  subject: string;
  body: string;
}

export const outreachService = {
  /** Mit `organizerId` nur die Kontakte dieses Veranstalters (Veranstalterseite). */
  contactsForOrganizer: async (organizerId: number): Promise<OutreachContact[]> => {
    const r = await api.get('/admin/outreach/contacts', { params: { organizerId } });
    return r.data;
  },

  team: async (): Promise<OutreachTeamMember[]> => {
    const r = await api.get('/admin/outreach/team');
    return r.data;
  },

  setOwner: async (contactId: number, ownerUserId: number | null): Promise<OutreachContact> => {
    const r = await api.put(`/admin/outreach/contacts/${contactId}/owner`, { ownerUserId });
    return r.data;
  },

  /** Antwortet mit der Listenzeile samt `unread`. */
  setRead: async (contactId: number, read: boolean): Promise<OutreachContact> => {
    const r = await api.post(`/admin/outreach/contacts/${contactId}/${read ? 'read' : 'unread'}`);
    return r.data;
  },

  notes: async (contactId: number): Promise<OutreachNote[]> => {
    const r = await api.get(`/admin/outreach/contacts/${contactId}/notes`);
    return r.data;
  },

  addNote: async (contactId: number, data: OutreachNoteRequest): Promise<OutreachNote> => {
    const r = await api.post(`/admin/outreach/contacts/${contactId}/notes`, data);
    return r.data;
  },

  updateNote: async (noteId: number, data: OutreachNoteRequest): Promise<OutreachNote> => {
    const r = await api.put(`/admin/outreach/notes/${noteId}`, data);
    return r.data;
  },

  deleteNote: async (noteId: number): Promise<void> => {
    await api.delete(`/admin/outreach/notes/${noteId}`);
  },

  listContacts: async (status?: OutreachContactStatus): Promise<OutreachContact[]> => {
    const params = status ? { status } : undefined;
    const r = await api.get('/admin/outreach/contacts', { params });
    return r.data;
  },
  getContact: async (id: number): Promise<OutreachContactDetail> => {
    const r = await api.get(`/admin/outreach/contacts/${id}`);
    return r.data;
  },
  createContact: async (data: CreateOutreachContactRequest): Promise<OutreachContact> => {
    const r = await api.post('/admin/outreach/contacts', data);
    return r.data;
  },
  updateContact: async (id: number, data: UpdateOutreachContactRequest): Promise<OutreachContact> => {
    const r = await api.put(`/admin/outreach/contacts/${id}`, data);
    return r.data;
  },
  /** Steht beim verknuepften Veranstalter etwas anderes? Leere Antwort = keine Verknuepfung. */
  organizerDiff: async (id: number): Promise<OutreachOrganizerDiff | null> => {
    const r = await api.get(`/admin/outreach/contacts/${id}/organizer-diff`);
    return r.data || null;
  },
  applyToOrganizer: async (id: number, fields: string[]): Promise<void> => {
    await api.post(`/admin/outreach/contacts/${id}/apply-to-organizer`, { fields });
  },
  deleteContact: async (id: number): Promise<void> => {
    await api.delete(`/admin/outreach/contacts/${id}`);
  },

  // --- Verlauf ---
  history: async (contactId: number): Promise<OutreachMessage[]> => {
    const r = await api.get(`/admin/outreach/contacts/${contactId}/messages`);
    return r.data;
  },
  renderTemplate: async (contactId: number, templateId: number): Promise<RenderedTemplate> => {
    const r = await api.get(`/admin/outreach/contacts/${contactId}/render/${templateId}`);
    return r.data;
  },
  /** Entwürfe des Schreib-Agenten (V173), jüngste zuerst. */
  drafts: async (contactId: number): Promise<OutreachDraft[]> => {
    const r = await api.get(`/admin/outreach/contacts/${contactId}/drafts`);
    return r.data;
  },
  /** Bestellt einen Entwurf. Läuft schon einer, kommt der zurück. */
  requestDraft: async (contactId: number, data: OutreachDraftRequest): Promise<OutreachDraft> => {
    const r = await api.post(`/admin/outreach/contacts/${contactId}/drafts`, data);
    return r.data;
  },
  discardDraft: async (draftId: number): Promise<OutreachDraft> => {
    const r = await api.post(`/admin/outreach/drafts/${draftId}/discard`);
    return r.data;
  },
  sendEmail: async (contactId: number, data: SendOutreachEmailRequest): Promise<OutreachMessage> => {
    const r = await api.post(`/admin/outreach/contacts/${contactId}/send`, data);
    return r.data;
  },
  /**
   * Eine Datei fuer die naechste Nachricht hochladen. Sie haengt danach an nichts — verknuepft
   * wird sie erst beim Senden ueber `attachmentFileIds`.
   */
  uploadFile: async (file: File): Promise<OutreachFile> => {
    const form = new FormData();
    form.append('file', file);
    const r = await api.post('/admin/outreach/files', form, {
      // Ohne den Header macht axios aus dem FormData JSON (api.ts setzt application/json als
      // Vorgabe) — der Datei-Teil fehlt dann, und der Auffang-Handler meldet einen 500.
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
  /** Absenderadresse und ob der Eingang laeuft — fuer den Hinweis ueber der Liste. */
  signature: async (): Promise<MailSignature> => {
    const r = await api.get('/admin/outreach/signature');
    return r.data;
  },

  saveSignature: async (fields: MailSignatureRequest): Promise<MailSignature> => {
    const r = await api.put('/admin/outreach/signature', fields);
    return r.data;
  },

  mailbox: async (): Promise<OutreachMailbox> => {
    const r = await api.get('/admin/outreach/mailbox');
    return r.data;
  },
  /** Offene Eingänge ohne erkennbaren Kontakt — mit `dismissed` stattdessen die weggelegten. */
  unassigned: async (dismissed = false): Promise<OutreachMessage[]> => {
    const r = await api.get('/admin/outreach/unassigned', { params: { dismissed } });
    return r.data;
  },
  /** Legt einen unzuordenbaren Eingang zur Seite: raus aus Liste und Badge, gelöscht wird nichts. */
  dismissMessage: async (messageId: number): Promise<OutreachMessage> => {
    const r = await api.post(`/admin/outreach/messages/${messageId}/dismiss`);
    return r.data;
  },
  restoreMessage: async (messageId: number): Promise<OutreachMessage> => {
    const r = await api.post(`/admin/outreach/messages/${messageId}/restore`);
    return r.data;
  },
  assignMessage: async (messageId: number, contactId: number): Promise<OutreachMessage> => {
    const r = await api.post(`/admin/outreach/messages/${messageId}/assign`, { contactId });
    return r.data;
  },
  unreadCount: async (): Promise<number> => {
    const r = await api.get('/admin/outreach/unread-count');
    return r.data.count ?? 0;
  },

  /**
   * Macht aus einem Veranstalter einen Kontakt (oder findet den vorhandenen). Verschickt nichts —
   * der Knopf „Anschreiben“ an der Veranstalter-Zeile öffnet damit nur den Verlauf.
   */
  contactFromOrganizer: async (organizerId: number): Promise<OutreachContact> => {
    const r = await api.post(`/admin/outreach/contacts/from-organizer/${organizerId}`);
    return r.data;
  },

  /** Übernimmt eine Anfrage aus dem öffentlichen Posteingang in den Verlauf. */
  importFromInquiry: async (inquiryId: number): Promise<OutreachContact> => {
    const r = await api.post(`/admin/outreach/from-inquiry/${inquiryId}`);
    return r.data;
  },

  // --- Sammelversand ---
  inviteOrganizers: async (data: InviteOrganizersRequest): Promise<InviteOrganizersResult> => {
    const r = await api.post('/admin/outreach/invite-organizers', data);
    return r.data;
  },

  // --- Sperrliste ---
  listSuppressions: async (): Promise<OutreachSuppression[]> => {
    const r = await api.get('/admin/outreach/suppressions');
    return r.data;
  },
  addSuppression: async (email: string, reason: OutreachSuppressionReason, note?: string): Promise<void> => {
    await api.post('/admin/outreach/suppressions', { email, reason, note });
  },
  removeSuppression: async (email: string): Promise<void> => {
    await api.delete(`/admin/outreach/suppressions/${encodeURIComponent(email)}`);
  },

  // --- Vorlagen ---
  placeholders: async (): Promise<OutreachPlaceholder[]> => {
    const r = await api.get('/admin/outreach/placeholders');
    return r.data;
  },
  listTemplates: async (): Promise<OutreachTemplate[]> => {
    const r = await api.get('/admin/outreach/templates');
    return r.data;
  },
  createTemplate: async (data: CreateOutreachTemplateRequest): Promise<OutreachTemplate> => {
    const r = await api.post('/admin/outreach/templates', data);
    return r.data;
  },
  updateTemplate: async (id: number, data: UpdateOutreachTemplateRequest): Promise<OutreachTemplate> => {
    const r = await api.put(`/admin/outreach/templates/${id}`, data);
    return r.data;
  },
  deleteTemplate: async (id: number): Promise<void> => {
    await api.delete(`/admin/outreach/templates/${id}`);
  },
  addTemplateAttachment: async (templateId: number, file: File): Promise<OutreachTemplate> => {
    const form = new FormData();
    form.append('file', file);
    const r = await api.post(`/admin/outreach/templates/${templateId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
  removeTemplateAttachment: async (templateId: number, attachmentId: number): Promise<OutreachTemplate> => {
    const r = await api.delete(`/admin/outreach/templates/${templateId}/attachments/${attachmentId}`);
    return r.data;
  },
};

export default outreachService;
